<template>
  <FormContainer @submitForm="saveInvestimento">
    <div class="row">
      <FormInputTextWidget
        class="col-lg-4 col-sm-6"
        label="Nome dell'investimento"
        v-model="investimento.name"
        :errorMsg="errorHash['name']"
        @input="removeError('name')"
      />

      <FormInputAmountWidget
        class="col-lg-4 col-sm-6"
        label="Importo"
        v-model.number="investimento.importo"
        :min="0"
        :errorMsg="errorHash['importo']"
        @input="removeError('importo')"
      />

      <FormInputSelectWidget
        class="col-lg-4 col-sm-6"
        label="Tipo di ammortamento fiscale"
        v-model="investimento.tipoAmmortamentoFiscaleId"
        :options="ammortamentoFiscaleOptions"
      />

      <FormInputPercentageWidget
        class="col-lg-4 col-sm-6"
        label="Aliquota IVA"
        v-model.number="investimento.vat"
        :errorMsg="errorHash['vat']"
        @input="removeError('vat')"
      />

      <FormInputIntegerWidget
        class="col-lg-4 col-sm-6"
        label="Mesi di ammortamento"
        v-model.number="investimento.nbMesiAmmortamento"
        :errorMsg="errorHash['nbMesiAmmortamento']"
        @input="removeError('nbMesiAmmortamento')"
      />

      <FormInputMonthPickerWidget
        class="col-lg-4 col-sm-6"
        label="Mese dell'investimento"
        v-model="investimento.begin"
        :errorMsg="errorHash['begin']"
        @input="removeError('begin')"
      />
    </div>

    <div class="row">
      <div class="col-12 col-xl-8 mt-4">
        <FormInputPlanWidget
          label="Piano dei pagamenti"
          v-model="pianoPagamenti"
          :total="importoDaScadenzare"
          :first-month="investimento.begin"
          :errorMsg="errorHash['pianoPagamenti']"
          @input="removeError('pianoPagamenti')"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-12 mt-4 text-center text-sm-right">
        <button class="btn btn-primary" type="submit">
          <i class="fas fa-save mr-2"></i>
          Salva investimento
        </button>
      </div>
    </div>
  </FormContainer>
</template>

<script>

import validateMixin from '@/libs/Form/mixins/validate';
import { arrayPlanToObjectPlan, getPlanTotal, objectPlanToArrayPlan, thereAreEmptyMonths } from '@/helpers/plans';

export default {
  name: 'operative-form-cost',
  mixins: [validateMixin],
  components: {
    FormContainer: () => import('@/libs/Form/components/FormContainer'),
    FormInputTextWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputTextWidget'),
    FormInputIntegerWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputIntegerWidget'),
    FormInputAmountWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputAmountWidget'),
    FormInputPercentageWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputPercentageWidget'),
    FormInputSelectWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputSelectWidget'),
    FormInputMonthPickerWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputMonthPickerWidget'),
    FormInputPlanWidget: () => import('@/views/components/Form/FormInputPlanWidget.vue'),
  },
  props: {
    investimento: {
      type: Object,
      default: () => ({
        name: null,
        importo: null,
        nbMesiAmmortamento: 60,
        tipoAmmortamentoFiscaleId: null,
        vat: 22,
        begin: null,
        pianoPagamenti: null,
      }),
    },
    ammortamentoFiscaleOptions: {
      type: Array,
      default: () => [
        { value: 1, label: 'Ordinario' },
        { value: 2, label: 'Superammortamento' },
        { value: 3, label: 'Iperammortamento' },
      ],
    },
    edit: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    pianoPagamenti: {
      get () {
        return objectPlanToArrayPlan(this.investimento.pianoPagamenti);
      },
      set (newVal) {
        this.investimento.pianoPagamenti = arrayPlanToObjectPlan(newVal);
      },
    },
    pianoPagamentiTotal () {
      return getPlanTotal(this.pianoPagamenti);
    },
    importoDaScadenzare () {
      if (this.investimento.importo === null || this.investimento.vat === null) {
        return null;
      }

      const importoDaScadenzare = (this.investimento.importo || 0) * (1 + (this.investimento.vat / 100));

      if (importoDaScadenzare === 0) {
        return null;
      }

      return importoDaScadenzare;
    },
  },
  methods: {
    validate () {
      this.clearErrors();

      if (!this.investimento.name) {
        this.addError('Il nome dell\'investimento è obbligatorio', 'name');
      }

      if (!this.investimento.importo) {
        this.addError('L\'importo dell\'investimento è obbligatorio', 'importo');
      }

      if (!this.investimento.vat && this.investimento.vat !== 0) {
        this.addError('L\'aliquota iva è obbligatoria', 'vat');
      }

      if (!this.investimento.nbMesiAmmortamento) {
        this.addError('Il numero di mesi di ammortamento è obbligatorio', 'nbMesiAmmortamento');
      }

      if (!this.investimento.begin) {
        this.addError('Il mese dell\'investimento è obbligatorio', 'begin');
      }

      if (this.importoDaScadenzare && this.investimento.begin) {
        if (!this.investimento.pianoPagamenti) {
          this.addError('Inserire il piano dei pagamenti', 'pianoPagamenti');
        } else if (this.pianoPagamentiTotal > this.importoDaScadenzare) {
          this.addError('Eccesso di pagamenti scadenziato', 'pianoPagamenti');
        } else if (this.pianoPagamentiTotal < this.importoDaScadenzare) {
          this.addError('Completare il piano dei pagamenti', 'pianoPagamenti');
        } else if (thereAreEmptyMonths(this.pianoPagamenti)) {
          this.addError('Completare il piano dei pagamenti', 'pianoPagamenti');
        }
      }

      return !this.hasErrors;
    },
    saveInvestimento () {
      if (!this.validate()) {
        return;
      }

      this.$emit('saved', this.investimento);
    },
  },
};

</script>
